import {
  memo,
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useState
} from 'react';

import { Button } from '@/shadcn/button';

import { Tray } from '@/components/ui/tray/tray';
import { TRAY_PORTAL_RIGHT } from '@/constant/htmlIds';

type Props = PropsWithChildren<{
  title: ReactNode;
  body: ReactNode;
  width?: number;
  collapsedWidth?: number;
  resizable?: boolean;
  minWidth?: number;
  setClientFocus: (focus: boolean) => void;
}>;

export const TrayWrapper = memo(
  ({
    children,
    title,
    body,
    width = 450, // width of the tray when uncollapsed
    collapsedWidth, // width of the tray when collapsed
    resizable, // whether the tray is resizable when uncollapsed
    minWidth, // minimun width if the tray is resizable
    setClientFocus
  }: Props) => {
    const [showTray, setShowTray] = useState(false);

    const openTray = useCallback((): void => {
      setShowTray(true);
      setClientFocus(true);
    }, []);

    const closeTray = useCallback((): void => {
      setShowTray(false);
      setClientFocus(false);
    }, []);

    // Marked as updated, if the widget wrap by the tray has an update
    // If we don't do that, the update mark will only be displayed when the tray is opened
    // @ts-expect-error
    if (body?.props) {
      // @ts-expect-error
      const { widget, update = {} } = body?.props ?? {};
      const viewPath = widget.view.path;

      if (update) {
        const { patch, setIsPatched } = update;

        if (patch && setIsPatched && viewPath in patch) {
          setIsPatched(true);
        }
      }
    }

    return (
      <>
        <div className="size-full" data-cy="open-tray" onClick={openTray}>
          {children}
        </div>
        {showTray && (
          <Tray
            collapsedWidth={collapsedWidth}
            minWidth={minWidth}
            onClose={closeTray}
            portalEl={document.getElementById(TRAY_PORTAL_RIGHT)}
            resizable={resizable}
            title={title}
            width={width}
          >
            <div className="flex h-full flex-col flex-nowrap text-[13px] text-card-foreground">
              <div className="h-full overflow-hidden">{body}</div>
              <div className="flex h-[70px] w-full shrink-0 flex-row flex-nowrap items-center justify-between p-3.5">
                <div className="flex flex-row flex-nowrap items-center justify-start">
                  <Button
                    aria-label="Close the tray"
                    disabled={false}
                    onClick={closeTray}
                    type="button"
                    variant="outline"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </Tray>
        )}
      </>
    );
  }
);
